import AppContext from 'App/AppContext';
import { IPCheckbox, IPScrollablePane, IPSeparator, IPText } from 'baseComponents';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import {
  AddNewCustomListButton,
  AddNewDocumentLibraryButton,
  AddNewProviderListButton,
  AddNewSitePageLibraryButton,
  AddNewUrlListButton,
} from 'components/Buttons/AddNewListButtons';
import GenericModal from 'components/Dialogs/GenericModal';
import ButtonDialogFooter from 'components/Footer/ButtonDialogFooter';
import ProviderFolderChooser from 'components/Links/Brokered/ProviderFolderChooser';
import { LearnMore } from 'components/Notification/Info';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import { globalKB_howToWorkWithTheDocumentLifeCycle } from 'globalConstants';
import { globalLayerRightSideBarProps, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { isDocumentProvidersEnabled } from 'models/copilot/copilotPreviewFeatureToggles';
import DocumentConnection from 'models/documentConnection';
import { DocumentProviderType, getListCapabilities } from 'models/documentProvider';
import ResourceList, { ResourceListSystemType, ResourceListType } from 'models/resourceList';
import { SharepointLibraries, SharepointPages } from 'models/setting';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IDriveItem, IList, ISite } from 'services/Graph/SharepointInterfaces';
import { getFullSiteId } from 'utils/sharepoint';
import { validateURL } from 'utils/url';
import {
  ChooseCustomList,
  ChooseDocumentLibraryList,
  ChooseSite,
  ChooseUrlList,
  ComboChooseSystemListType,
} from './AddNewListPanelContent';

interface IAddNewListPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  onAddNew: (item: ResourceList) => void;
  //false renders the form in a modal, for a host that is a modal itself: a panel would open behind it
  renderAsPanel?: boolean;
}

const AddNewListPanel = (props: IAddNewListPanelProps) => {
  const { t } = useTranslation(['translation', 'lists', 'documentproviders']);
  const appContext = useContext(AppContext);
  const [newList, setNewList] = useState<ResourceList>(new ResourceList());
  const [nameError, setNameError] = useState<boolean>(false);
  const [selectedFolder, setSelectedFolder] = useState<IDriveItem | undefined>(undefined);
  const [selectedList, setSelectedList] = useState<IList | undefined>(undefined);
  const [selectedSite, setSelectedSite] = useState<ISite | undefined>(undefined);
  const [webUrl, setWebUrl] = useState<string | undefined>(undefined);

  const typeDocumentEnabled = (): boolean => {
    return appContext.globalDataCache.settings.get(SharepointLibraries);
  };

  const typeListEnabled = (): boolean => {
    return appContext.globalDataCache.settings.get(SharepointPages);
  };

  const typePageEnabled = (): boolean => {
    return appContext.globalDataCache.settings.get(SharepointPages);
  };

  const [addMode, setAddMode] = useState<'document' | 'custom' | 'url' | 'page' | 'provider' | undefined>(undefined);
  // a category on a document provider: which connection, and where inside it
  const [providerConnection, setProviderConnection] = useState<DocumentConnection | undefined>(undefined);
  const [providerContainerId, setProviderContainerId] = useState<string | undefined>(undefined);
  const [providerFolderId, setProviderFolderId] = useState<string | undefined>(undefined);
  const [providerFolderName, setProviderFolderName] = useState<string>('');

  const documentProvidersEnabled = isDocumentProvidersEnabled(
    appContext.globalDataCache.settings,
    appContext.user.tenant.identityProviderType,
  );
  const providerConnections = documentProvidersEnabled
    ? appContext.globalDataCache.documentConnections.getUsable()
    : [];
  // connections exist but every one is switched off: without a word the provider button just
  // vanishes and the user cannot tell why
  const onlyDisabledConnections =
    documentProvidersEnabled &&
    providerConnections.length === 0 &&
    appContext.globalDataCache.documentConnections.items.length > 0;

  const disableSave = (): boolean => {
    if (nameError) return true;

    // a provider category is bound by its connection and the folder picked inside it; the
    // listType checks below are about the SharePoint pickers, which it never uses
    if (addMode === 'provider') {
      return !providerConnection || !providerContainerId;
    }

    if (newList.listType === ResourceListType.DocumentLibrary) {
      if (!selectedFolder) {
        return true;
      }
    } else if (newList.listType === ResourceListType.CustomList) {
      if (!selectedList) {
        return true;
      }
    } else if (newList.listType === ResourceListType.SitePageLibrary) {
      if (!selectedSite) {
        return true;
      }
    } else if (newList.listType === ResourceListType.WebURL) {
      if (webUrl) {
        return !!webUrl && !validateURL(webUrl);
      }
    }

    return false;
  };

  const setName = (newValue?: string) => {
    const listCopy = newList.clone();
    listCopy.name = newValue ?? '';
    setNewList(listCopy);
  };

  const setAuthSchema = (newValue: number | undefined) => {
    const listCopy = newList.clone();
    listCopy.authSchemaId = newValue;
    setNewList(listCopy);
  };

  const setMetaDataEnabled = (checked: boolean) => {
    const listCopy = newList.clone();
    listCopy.enableMetaData = checked;
    setNewList(listCopy);
  };

  const setSkipPreviewEnabled = (checked: boolean) => {
    const listCopy = newList.clone();
    listCopy.skipPreview = checked;
    setNewList(listCopy);
  };

  const setSystemType = (newValue?: ResourceListSystemType) => {
    const listCopy = newList.clone();
    listCopy.systemListId = newValue;
    setNewList(listCopy);
  };

  const onRenderFooterContent = () => (
    <ButtonDialogFooter
      disablePrimary={disableSave}
      primaryText={t('translation:General.Button.Save')}
      secondaryText={t('translation:General.Button.Cancel')}
      onCancel={props.onDismiss}
      onSubmit={() => {
        props.onAddNew(newList);
        props.onDismiss();
      }}
      buttonPosition="start"
    />
  );

  useEffect(() => {
    const copyNewList = newList.clone();

    if (addMode === 'document') {
      copyNewList.webURL = selectedFolder?.webUrl ?? '';
      copyNewList.spDriveId = selectedFolder?.parentReference?.driveId ?? '';
      copyNewList.spDriveItemId = selectedFolder?.id ?? '';
      copyNewList.listType = ResourceListType.DocumentLibrary;
      copyNewList.altTenantId = selectedFolder?.tenantId;

      copyNewList.spListId = selectedFolder?.sharepointIds?.listId || undefined;
      copyNewList.spSiteId = getFullSiteId(selectedFolder);
    } else if (addMode === 'custom') {
      copyNewList.webURL = selectedList?.webUrl ?? '';
      copyNewList.spListId = selectedList?.id ?? '';
      copyNewList.spSiteId = selectedList?.parentReference?.siteId ?? '';
      copyNewList.listType = ResourceListType.CustomList;
      copyNewList.altTenantId = selectedList?.tenantId;

      copyNewList.spDriveId = undefined;
      copyNewList.spDriveItemId = undefined;
    } else if (addMode === 'page') {
      copyNewList.webURL = selectedSite?.webUrl ?? '';
      copyNewList.spSiteId = selectedSite?.id ?? '';
      copyNewList.listType = ResourceListType.SitePageLibrary;
      copyNewList.altTenantId = selectedSite?.tenantId;

      copyNewList.spListId = undefined;
      copyNewList.spDriveId = undefined;
      copyNewList.spDriveItemId = undefined;
    } else if (addMode === 'url') {
      copyNewList.webURL = webUrl;
      copyNewList.listType = ResourceListType.WebURL;

      copyNewList.spDriveId = undefined;
      copyNewList.spDriveItemId = undefined;
      copyNewList.spListId = undefined;
      copyNewList.spSiteId = undefined;
    } else if (addMode === 'provider') {
      // the provider's ids ride in the same columns SharePoint uses: the container in
      // spDriveId and the root folder in spDriveItemId
      copyNewList.providerType = providerConnection?.providerType ?? DocumentProviderType.SharePoint;
      copyNewList.connectionId = providerConnection?.documentConnectionId;
      copyNewList.spDriveId = providerContainerId;
      copyNewList.spDriveItemId = providerFolderId;
      // a wiki provider holds pages, everything else files
      copyNewList.listType =
        getListCapabilities(copyNewList, appContext.globalDataCache).pagesKind === true
          ? ResourceListType.SitePageLibrary
          : ResourceListType.DocumentLibrary;
      // no provider outside SharePoint has a version model, so publication stays off
      copyNewList.enableMetaData = false;

      copyNewList.spListId = undefined;
      copyNewList.spSiteId = undefined;
      copyNewList.webURL = '';
    }

    if (copyNewList.altTenantId) {
      const tenant = appContext.user.login.tenants?.find((t) => t.providerTenantId === copyNewList.altTenantId);
      copyNewList.altTenantName = tenant?.name;
    }

    setNewList(copyNewList);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder, selectedList, selectedSite, webUrl, addMode, providerConnection, providerContainerId, providerFolderId]);

  useEffect(() => {
    if (addMode === 'custom') {
      setSelectedFolder(undefined);
      setWebUrl('');
    } else if (addMode === 'document') {
      setSelectedList(undefined);
      setWebUrl('');
    } else if (addMode === 'page') {
      setSelectedSite(undefined);
      setWebUrl('');
    } else {
      setSelectedFolder(undefined);
      setSelectedList(undefined);
    }
  }, [addMode]);

  const setNewListType = (type: 'document' | 'custom' | 'url' | 'page' | 'provider') => {
    setAddMode(type);
    setName('');

    if (type !== 'provider') {
      setProviderConnection(undefined);
      setProviderContainerId(undefined);
      setProviderFolderId(undefined);
      setProviderFolderName('');
    }
  };

  //
  // Main render
  //
  const body = (
    <IPStack tokens={globalStackTokensGapMedium} styles={{ root: { marginTop: 20 } }}>
      <IPStack.Item>
        <IPText>{t('lists:AddNew.ChooseCategoryType')}</IPText>
        <LearnMore learnMoreLink={globalKB_howToWorkWithTheDocumentLifeCycle} />
      </IPStack.Item>
      <IPStack.Item>
        <IPStack horizontal wrap tokens={globalStackTokensGapSmall} styles={{ root: { paddingRight: 10 } }}>
          <AddNewDocumentLibraryButton
            disabled={!typeDocumentEnabled()}
            selected={addMode === 'document'}
            onClick={() => setNewListType('document')}
          />
          <AddNewSitePageLibraryButton
            disabled={!typePageEnabled()}
            selected={addMode === 'page'}
            onClick={() => setNewListType('page')}
          />
          <AddNewCustomListButton
            disabled={!typeListEnabled()}
            selected={addMode === 'custom'}
            onClick={() => setNewListType('custom')}
          />
          <AddNewUrlListButton selected={addMode === 'url'} onClick={() => setNewListType('url')} />
          {providerConnections.length > 0 && (
            <AddNewProviderListButton selected={addMode === 'provider'} onClick={() => setNewListType('provider')} />
          )}
        </IPStack>
      </IPStack.Item>
      {onlyDisabledConnections && (
        <IPStack.Item>
          <IPText variant="small">{t('documentproviders:Category.NoUsableConnection')}</IPText>
        </IPStack.Item>
      )}
      <IPSeparator />
      {addMode === 'document' && (
        <ChooseDocumentLibraryList
          folder={selectedFolder}
          selectFolder={(item: IDriveItem) => {
            setSelectedFolder(item);
            setName(item.name ?? '');
          }}
        />
      )}
      {addMode === 'custom' && (
        <ChooseCustomList
          list={selectedList}
          selectList={(item: IList) => {
            setSelectedList(item);
            setName(item.name ?? '');
          }}
        />
      )}
      {addMode === 'page' && (
        <ChooseSite
          site={selectedSite}
          selectSite={(item: ISite) => {
            setSelectedSite(item);
            setName(item.name ?? '');
          }}
        />
      )}
      {addMode === 'url' && <ChooseUrlList webUrl={webUrl} setUrl={setWebUrl} />}
      {addMode === 'provider' && (
        <ProviderFolderChooser
          connections={providerConnections}
          connection={providerConnection}
          folderName={providerFolderName}
          selectConnection={(connection: DocumentConnection) => {
            setProviderConnection(connection);
            setProviderContainerId(undefined);
            setProviderFolderId(undefined);
            setProviderFolderName('');
          }}
          selectFolder={(containerId: string | undefined, folderId: string | undefined, name: string) => {
            setProviderContainerId(containerId);
            setProviderFolderId(folderId);
            setProviderFolderName(name);
            setName(name);
          }}
        />
      )}
      {addMode !== undefined &&
        (selectedSite ||
          selectedList ||
          selectedFolder ||
          addMode === 'url' ||
          (addMode === 'provider' && !!providerContainerId)) && (
        <Fragment>
          <IPStack.Item>
            <IPTextField
              value={newList.name}
              placeholder=""
              label={t('lists:AddNew.Name')}
              onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                setName(newValue?.trimStart());
              }}
              onGetErrorMessage={async (value: string): Promise<string> => {
                if (!newList) return '';
                if (value === undefined || value.trimStart() === '') {
                  setNameError(true);

                  return t('lists:AddNew.NoNameError');
                }
                const existingId = await ResourceList.nameExists(value.toLowerCase(), appContext);
                if (existingId) {
                  setNameError(true);

                  return t('lists:AddNew.DuplicateNameError');
                }
                setNameError(false);

                return '';
              }}
            />
          </IPStack.Item>
          {(newList.listType === ResourceListType.DocumentLibrary ||
            newList.listType === ResourceListType.SitePageLibrary) && (
            <IPStack.Item>
              {/* no provider outside SharePoint has a version model, and the server forces
                  this off for them, so offering the box would promise something a save
                  silently takes away again */}
              <IPCheckbox
                checked={newList.enableMetaData}
                disabled={!getListCapabilities(newList, appContext.globalDataCache).versions}
                label={t('lists:Edit.EnableMetaData')}
                onChange={(ev, checked) => {
                  setMetaDataEnabled(checked ?? false);
                }}
              />
            </IPStack.Item>
          )}
          {newList.listType === ResourceListType.WebURL && (
            <IPStack.Item>
              <IPCheckbox
                checked={newList.skipPreview}
                label={t('lists:Edit.SkipPreview')}
                onChange={(ev, checked) => {
                  setSkipPreviewEnabled(checked ?? false);
                }}
              />
            </IPStack.Item>
          )}
          <IPStack.Item>
            <ComboChooseSystemListType
              selectedKey={newList.systemListId}
              onChange={(systemType) => setSystemType(systemType)}
            />
          </IPStack.Item>
          <IPStack.Item>
            <AuthSchemaPicker
              selectedAuthSchemaId={newList.authSchemaId}
              onSelect={(schema) => setAuthSchema(schema?.authSchemaId)}
            />
          </IPStack.Item>
        </Fragment>
      )}
    </IPStack>
  );

  if (props.renderAsPanel ?? true) {
    return (
      <IPPanel
        type={IPPanelType.medium}
        isBlocking
        isOpen={props.isOpen}
        onDismiss={props.onDismiss}
        headerText={t('lists:AddNew.Title')}
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom={true}
        layerProps={globalLayerRightSideBarProps}
      >
        {body}
      </IPPanel>
    );
  }

  return (
    <GenericModal
      title={t('lists:AddNew.Title')}
      isOpen={props.isOpen}
      onClose={props.onDismiss}
      onSave={() => {
        props.onAddNew(newList);
        props.onDismiss();
      }}
      keepOpenAfterSave
      saveDisabled={disableSave()}
      width={720}
      maxWidth={'min(92vw, 720px)'}
      height={'90vh'}
      maxHeight={'90vh'}
      titleSeparator
    >
      {/* the modal has a fixed height, so only the form scrolls and the title and Save stay in view */}
      <IPStack verticalFill>
        <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
          <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>{body}</IPScrollablePane>
        </IPStack.Item>
      </IPStack>
    </GenericModal>
  );
};

export default AddNewListPanel;
